/**
 * A tiny, dependency-free XML reader/writer.
 *
 * DayZ mission files are a small, well-behaved subset of XML, and the browser's
 * DOMParser is not available when running the unit tests under Node, so this
 * module implements just enough of the format to round-trip the mission files
 * without losing comments, attribute order or unknown elements.
 */

export type XAttr = { name: string; value: string };

export type XElement = {
  kind: 'element';
  id: string;
  tag: string;
  attrs: XAttr[];
  children: XNode[];
};

export type XText = { kind: 'text'; id: string; value: string };
export type XComment = { kind: 'comment'; id: string; value: string };
/** Anything we keep verbatim: the `<?xml ... ?>` declaration, doctypes, CDATA. */
export type XRaw = { kind: 'raw'; id: string; value: string };

export type XNode = XElement | XText | XComment | XRaw;

export type XDocument = {
  /** Declaration / comments before the root element, the root, then trailing nodes. */
  nodes: XNode[];
};

let uid = 0;
export function nextId(prefix = 'n'): string {
  uid += 1;
  return `${prefix}${uid}`;
}

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
};

export function decodeEntities(input: string): string {
  return input.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, body: string) => {
    if (body[0] === '#') {
      const code =
        body[1] === 'x' || body[1] === 'X'
          ? Number.parseInt(body.slice(2), 16)
          : Number.parseInt(body.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : match;
    }
    const named = ENTITIES[body];
    return named === undefined ? match : named;
  });
}

export function encodeText(input: string): string {
  return input.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));
}

export function encodeAttr(input: string): string {
  return input.replace(/[&<>"]/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;',
  );
}

export class XmlParseError extends Error {}

/** Parses an XML string into a node tree. Throws {@link XmlParseError} on malformed input. */
export function parseXml(source: string): XDocument {
  let i = 0;
  const nodes: XNode[] = [];
  const stack: XElement[] = [];

  const push = (node: XNode) => {
    const parent = stack[stack.length - 1];
    if (parent) parent.children.push(node);
    else nodes.push(node);
  };

  while (i < source.length) {
    const lt = source.indexOf('<', i);
    if (lt === -1) {
      pushText(source.slice(i));
      break;
    }
    if (lt > i) pushText(source.slice(i, lt));

    if (source.startsWith('<!--', lt)) {
      const end = source.indexOf('-->', lt);
      if (end === -1) throw new XmlParseError('Unterminated comment');
      push({ kind: 'comment', id: nextId('c'), value: source.slice(lt + 4, end) });
      i = end + 3;
      continue;
    }
    if (source.startsWith('<?', lt)) {
      const end = source.indexOf('?>', lt);
      if (end === -1) throw new XmlParseError('Unterminated processing instruction');
      push({ kind: 'raw', id: nextId('r'), value: source.slice(lt, end + 2) });
      i = end + 2;
      continue;
    }
    if (source.startsWith('<![CDATA[', lt)) {
      const end = source.indexOf(']]>', lt);
      if (end === -1) throw new XmlParseError('Unterminated CDATA section');
      push({ kind: 'raw', id: nextId('r'), value: source.slice(lt, end + 3) });
      i = end + 3;
      continue;
    }
    if (source.startsWith('<!', lt)) {
      const end = source.indexOf('>', lt);
      if (end === -1) throw new XmlParseError('Unterminated declaration');
      push({ kind: 'raw', id: nextId('r'), value: source.slice(lt, end + 1) });
      i = end + 1;
      continue;
    }
    if (source.startsWith('</', lt)) {
      const end = source.indexOf('>', lt);
      if (end === -1) throw new XmlParseError('Unterminated closing tag');
      const tag = source.slice(lt + 2, end).trim();
      const open = stack.pop();
      if (!open) throw new XmlParseError(`Unexpected closing tag </${tag}>`);
      if (open.tag !== tag) {
        throw new XmlParseError(`Closing tag </${tag}> does not match <${open.tag}>`);
      }
      i = end + 1;
      continue;
    }

    // Opening tag.
    const parsed = parseStartTag(source, lt);
    push(parsed.element);
    if (!parsed.selfClosing) stack.push(parsed.element);
    i = parsed.end;
  }

  if (stack.length > 0) throw new XmlParseError(`Unclosed element <${stack[stack.length - 1].tag}>`);
  return { nodes };

  function pushText(value: string) {
    if (value.length === 0) return;
    push({ kind: 'text', id: nextId('t'), value: decodeEntities(value) });
  }
}

function parseStartTag(source: string, start: number): { element: XElement; end: number; selfClosing: boolean } {
  let i = start + 1;
  const nameMatch = /^[^\s/>]+/.exec(source.slice(i));
  if (!nameMatch) throw new XmlParseError(`Malformed tag at offset ${start}`);
  const tag = nameMatch[0];
  i += tag.length;

  const attrs: XAttr[] = [];
  let selfClosing = false;

  for (;;) {
    while (i < source.length && /\s/.test(source[i])) i += 1;
    if (i >= source.length) throw new XmlParseError(`Unterminated tag <${tag}>`);
    if (source[i] === '>') {
      i += 1;
      break;
    }
    if (source.startsWith('/>', i)) {
      selfClosing = true;
      i += 2;
      break;
    }
    const attrMatch = /^([^\s=/>]+)\s*(=\s*("([^"]*)"|'([^']*)'))?/.exec(source.slice(i));
    if (!attrMatch) throw new XmlParseError(`Malformed attribute in <${tag}>`);
    const raw = attrMatch[4] ?? attrMatch[5] ?? '';
    attrs.push({ name: attrMatch[1], value: decodeEntities(raw) });
    i += attrMatch[0].length;
  }

  return { element: { kind: 'element', id: nextId('e'), tag, attrs, children: [] }, end: i, selfClosing };
}

export type SerializeOptions = {
  /** Indentation unit. Vanilla DayZ mission files use tabs. */
  indent?: string;
  /** Line ending. Vanilla files ship with CRLF; LF keeps diffs tidy. */
  eol?: string;
};

/** Serializes a document back to text, re-indenting the structural whitespace. */
export function serializeXml(doc: XDocument, options: SerializeOptions = {}): string {
  const indent = options.indent ?? '\t';
  const eol = options.eol ?? '\n';
  const lines: string[] = [];

  for (const node of doc.nodes) writeNode(node, 0);
  return lines.join(eol) + eol;

  function writeNode(node: XNode, depth: number) {
    const pad = indent.repeat(depth);
    switch (node.kind) {
      case 'raw':
        lines.push(pad + node.value);
        return;
      case 'comment':
        lines.push(`${pad}<!--${node.value}-->`);
        return;
      case 'text': {
        const trimmed = node.value.trim();
        if (trimmed.length > 0) lines.push(pad + encodeText(trimmed));
        return;
      }
      case 'element': {
        const open = `<${node.tag}${node.attrs
          .map((a) => ` ${a.name}="${encodeAttr(a.value)}"`)
          .join('')}`;
        const meaningful = node.children.filter(
          (child) => child.kind !== 'text' || child.value.trim().length > 0,
        );
        if (meaningful.length === 0) {
          // Vanilla mission files put a space before the slash; matching that
          // keeps diffs against the original files minimal.
          lines.push(`${pad}${open} />`);
          return;
        }
        // `<nominal>7</nominal>` and friends stay on one line.
        if (meaningful.length === 1 && meaningful[0].kind === 'text') {
          lines.push(`${pad}${open}>${encodeText(meaningful[0].value.trim())}</${node.tag}>`);
          return;
        }
        lines.push(`${pad}${open}>`);
        for (const child of meaningful) writeNode(child, depth + 1);
        lines.push(`${pad}</${node.tag}>`);
      }
    }
  }
}

/* ------------------------------------------------------------------ */
/* Tree helpers                                                        */
/* ------------------------------------------------------------------ */

export function rootElement(doc: XDocument): XElement | null {
  for (const node of doc.nodes) if (node.kind === 'element') return node;
  return null;
}

export function getAttr(el: XElement, name: string): string | undefined {
  return el.attrs.find((a) => a.name === name)?.value;
}

export function getNumAttr(el: XElement, name: string): number | undefined {
  const raw = getAttr(el, name);
  if (raw === undefined) return undefined;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : undefined;
}

export function element(tag: string, attrs: Record<string, string>, children: XNode[] = []): XElement {
  return {
    kind: 'element',
    id: nextId('e'),
    tag,
    attrs: Object.entries(attrs).map(([name, value]) => ({ name, value })),
    children,
  };
}

export function textElement(tag: string, value: string): XElement {
  return element(tag, {}, [{ kind: 'text', id: nextId('t'), value }]);
}

/** Depth-first walk over every element in the document. */
export function* walkElements(doc: XDocument): Generator<{ el: XElement; parent: XElement | null; depth: number }> {
  function* visit(node: XNode, parent: XElement | null, depth: number): Generator<{ el: XElement; parent: XElement | null; depth: number }> {
    if (node.kind !== 'element') return;
    yield { el: node, parent, depth };
    for (const child of node.children) yield* visit(child, node, depth + 1);
  }
  for (const node of doc.nodes) yield* visit(node, null, 0);
}

export function findElementById(doc: XDocument, id: string): XElement | null {
  for (const { el } of walkElements(doc)) if (el.id === id) return el;
  return null;
}

/** Returns the child text of an element, e.g. `7` for `<nominal>7</nominal>`. */
export function elementText(el: XElement): string {
  return el.children
    .filter((c): c is XText => c.kind === 'text')
    .map((c) => c.value)
    .join('')
    .trim();
}

export function setElementText(el: XElement, value: string): void {
  el.children = [{ kind: 'text', id: nextId('t'), value }];
}

export function setAttr(el: XElement, name: string, value: string): void {
  const existing = el.attrs.find((a) => a.name === name);
  if (existing) existing.value = value;
  else el.attrs.push({ name, value });
}

export function removeAttr(el: XElement, name: string): void {
  el.attrs = el.attrs.filter((a) => a.name !== name);
}

/** Deep clone that assigns fresh ids, used by undo snapshots and duplication. */
export function cloneNode<T extends XNode>(node: T): T {
  switch (node.kind) {
    case 'element':
      return {
        kind: 'element',
        id: nextId('e'),
        tag: node.tag,
        attrs: node.attrs.map((a) => ({ ...a })),
        children: node.children.map(cloneNode),
      } as T;
    default:
      return { ...node, id: nextId(node.kind[0]) } as T;
  }
}

export function cloneDocument(doc: XDocument): XDocument {
  return { nodes: doc.nodes.map(cloneNode) };
}

/** Clone that keeps node ids, so selections survive an undo/redo snapshot. */
export function cloneDocumentPreservingIds(doc: XDocument): XDocument {
  const copy = (node: XNode): XNode =>
    node.kind === 'element'
      ? { ...node, attrs: node.attrs.map((a) => ({ ...a })), children: node.children.map(copy) }
      : { ...node };
  return { nodes: doc.nodes.map(copy) };
}

export function removeElements(doc: XDocument, ids: Set<string>): number {
  let removed = 0;
  const filter = (nodes: XNode[]): XNode[] =>
    nodes.filter((node) => {
      if (node.kind === 'element') {
        if (ids.has(node.id)) {
          removed += 1;
          return false;
        }
        node.children = filter(node.children);
      }
      return true;
    });
  doc.nodes = filter(doc.nodes);
  return removed;
}
